import React, { useRef } from "react";
import { RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip } from "react-common-components";
import AccordionList from "src/components/AccordionList";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useTranslation } from "src/context/Translations/context";
import ListOfActions, { IRefListOfActions } from "src/pages/Actions/Panel/listOfActions";
import IFiltersActionsPanel, { ActionGroupers } from "src/pages/Actions/Panel/interface";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import { useAuth } from "src/context/Auth/context";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { IKanbanType } from "src/pages/Actions/Kanban/kanbanModes/MultipleKanbans/interface";
import ListProjectsLevel from "./Components/ListProjectsLevel";
import { IListPhase, PhaseAgile } from "./interface";

const ListPhase: React.FC<IListPhase> = ({
	project,
	phase,
	phaseAgile,
	onCallbackProgram,
	openModalAction,
	openModalChangeSprint,
	openModalDetails,
	openModalReleaseActions,
	requestQuery,
	openModalGantt
}) => {
	const { translate } = useTranslation();
	const {confirm} = useConfirmModal();
	const {addToast} = RCCHookToast();
	const { superUser, administrator } = useAuth();

	const listOfActionsRef = useRef<IRefListOfActions>(null);

	const handleClickGroupers = async (_e: any, expanded: boolean) => {
		if(!expanded)
			return;

		listOfActionsRef.current?.executeQuery();
	};

	const deleteSprint = (phaseId: number) => {
		confirm(
			translate("Exclusão de Sprint"), 
			translate("Deseja realmente excluir a Sprint?"),
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "ProjectPhase/Delete",
					urlParameters: {
						id: phaseId
					}
				});

				onCallbackProgram(project.pra002Pra001Id);

				addToast({
					color: "green",
					description: translate("Sprint excluída com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Sprint"), addToast);
			},
			true
		);
	};

	const options = (row: any) => (
		<RCCGrid container item justifyContent="flex-end">
			<RCCTooltip
				color="grey" 
				text={`${translate("Visualizar")} Gantt`}
			>
				<RCCButton 
					color="transparent"
					icon={{
						name: "table",
						color: "var(--input-font-color)",
						size: 14
					}}
					styleInline="padding: 5px 5px;"
					onClick={(e) => {e.stopPropagation(); if(!openModalGantt) return; openModalGantt(row.id, `${translate("Fase")}: ${row.name}`, "phase");}}
				/>
			</RCCTooltip>
							
			{phaseAgile && project.pra002Kba001Id && (
				<RCCTooltip
					color="grey" 
					text={translate("Abrir Kanban")}
				>
					<RCCButton 
						color="transparent"
						icon={{
							name: "kanban",
							color: "var(--input-font-color)",
							size: 14
						}}
						styleInline="padding: 5px 5px;"
						onClick={(e) => { 
							window.open(`/actions/kanban/${project.pra002Kba001Id}?association=${IKanbanType.PROJECT}&associationId=${project.pra002Id}&associationId2=${row.id}`);
							e.stopPropagation(); 
						}}
					/>
				</RCCTooltip>
			)}
			<OptionsColumn useStopPropagation>
						
				{!phaseAgile &&
							<RCCDropDownItem 
								selfEnclose
								onClick={(e) => {e.stopPropagation(); if(!openModalDetails) return; openModalDetails(row.id, "phase", row.name, project.pra002ControlaHoras === "S");}}
							>
								<RCCIcon name="info" />
								{translate("Detalhes")}
							</RCCDropDownItem>
				}

				{(superUser || administrator || row.permission >= 2) && (
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCDropDownItem
								selfEnclose
								onClick={(e) => {
									e.stopPropagation();
									if(!openModalAction) 
			 							return; 

									const dateStart = phase.pra003Dtiniest ? new Date(new Date(phase.pra003Dtiniest).toDateString()): new Date();
									const dateEnd = phase.pra003Dtfimest ? new Date(new Date(phase.pra003Dtfimest).toDateString()): new Date();
									openModalAction(undefined, callbackActionAdd, undefined, undefined, {projectPhase: row.id}, undefined, {startDate: dateStart, endDate: dateEnd});
								}}
							>
								<RCCIcon name="plus" />
								{translate("Adicionar Ação")}
							</RCCDropDownItem>
						</CheckPermission>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							{phaseAgile && phaseAgile === PhaseAgile.SPRINT &&
									<RCCDropDownItem
										selfEnclose
										onClick={(e) => {e.stopPropagation(); if(!openModalChangeSprint) return; openModalChangeSprint(row.id, project);}}
									>
										<RCCIcon name="pencil" />
										{translate("Editar Sprint")}
									</RCCDropDownItem>
							}
						</CheckPermission>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
							{phaseAgile && phaseAgile === PhaseAgile.SPRINT &&
						<RCCDropDownItem
							selfEnclose
							onClick={(e) => {e.stopPropagation(); deleteSprint(row.id);}}
						>
							<RCCIcon name="trash" />
							{translate("Excluir Sprint")}
						</RCCDropDownItem>
							}
						</CheckPermission>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							{phase.actionsInDraft &&
						<RCCDropDownItem
							selfEnclose
							onClick={(e) => {e.stopPropagation(); if(!openModalReleaseActions) return; openModalReleaseActions(phase, callbackActionAdd);}}
						>
							<RCCIcon name="listCheck" />
							{translate("Liberar Ações em Rascunho")}
						</RCCDropDownItem>
							}
						</CheckPermission>
					</>
				)}
			
			</OptionsColumn>
		</RCCGrid>
	);

	const callbackActionAdd = () => {
		onCallbackProgram(project.pra002Pra001Id);
		listOfActionsRef.current?.executeQuery();
	};

	const callBackChangeActionSprint = async () => {
		if(requestQuery)
			requestQuery(project.pra002Id);	
	};

	return (
		<AccordionList 
			showExpandIcon={false}
			onChange={(e, expanded) => handleClickGroupers(e, expanded)}
			header={
				<ListProjectsLevel
					imageOnly
					showHeader={false}
					data={[{
						id: phase.pra003Id,
						level: 2,
						name: phase.pra003Titulo,
						users: phase.users,
						startDate: phase.pra003Dtiniest,
						endDate: phase.pra003Dtfimest,
						perConclusion: phase.completionPercentage,
						perConclusionColor: phase.completionPercentageColor,
						perPrevision: phase.expectedPercentage,
						count: phase.count,
						completed: phase.completed,
						agile: phaseAgile,
						permission: phase.permission
					}]}
					options={options}
				/>}
		>
			<div className="container-actions">

				<ListOfActions 
					onCallbackUpdate={() => {onCallbackProgram(project.pra002Pra001Id);}}
					onCallbackDelete={() => {onCallbackProgram(project.pra002Pra001Id);}}
					callBackChangeActionSprint={callBackChangeActionSprint}
					ref={listOfActionsRef}
					layoutList="list"
					filters={{
						notValidateRaci: true
					} as IFiltersActionsPanel }
					groupers={{
						grouperId: project.pra002Pra001Id,
						grouperId2: project.pra002Id,
						grouperId3: phase.pra003Id
					}}
					groupersSelect={{
						group: ActionGroupers.PROGRAM_PROJECT_PHASE,
						delayed: "",
						origin: ""
					}}
					layout={phaseAgile ? "projectAgile" : "project"}  
				/>
								
			</div>
		</AccordionList>
		
	);
};

export default ListPhase;
